import { api, call } from './client'
import type { ApiResult, Schema } from './client'

/**
 * The organisation's logo: the rule, and the two calls that write it.
 *
 * Shared by the signup form (spec: *Signup* — the logo is the fifth field)
 * and Settings → Branding, because they send the same file to the same route
 * under the same limits, and two copies of a limit are a limit that drifts.
 *
 * `PUT /tenants/{id}/logo` is **the one `multipart/form-data` body in the API**
 * (`app/v1/tenants.py`): the browser already holds the picked file as bytes,
 * and a base64 field would cost an encode and a third more on the wire.
 * `openapi-fetch` serialises a body through `bodySerializer`, and hands a
 * `FormData` to `fetch` untouched — the browser writes the boundary.
 */

/** `MAX_LOGO_BYTES` in `app/tenancy/branding.py`. */
export const MAX_LOGO_BYTES = 256 * 1024

/** The `accept` attribute for the picker: the four types `validate_logo` takes. */
export const LOGO_ACCEPT = 'image/png,image/svg+xml,image/jpeg,image/webp'

const LOGO_TYPES: ReadonlySet<string> = new Set(LOGO_ACCEPT.split(','))

/** The rule, as one sentence for a help line. */
export const LOGO_RULE = 'PNG, SVG, JPEG or WebP, up to 256 KB.'

/**
 * Why a picked file cannot be sent, or `null` when it can.
 *
 * Checked before the request, so a 5 MB photograph is refused beside the
 * picker rather than after a round trip that ends in a 422 naming the limit.
 * The declared type is what the browser reports from the extension; the API
 * sniffs the bytes and is the authority — this stops the obvious cases only.
 */
export function logoProblem(file: File): string | null {
  if (!LOGO_TYPES.has(file.type)) {
    return 'That file is not a PNG, SVG, JPEG or WebP image.'
  }
  if (file.size === 0) return 'That file is empty.'
  if (file.size > MAX_LOGO_BYTES) {
    return `That file is ${formatKilobytes(file.size)} — the limit is 256 KB.`
  }
  return null
}

function formatKilobytes(bytes: number): string {
  return `${String(Math.ceil(bytes / 1024))} KB`
}

/** Store the logo. Answers the organisation with its new `logo_url`. */
export function putLogo(
  tenantId: string,
  file: File,
): Promise<ApiResult<Schema<'TenantResponse'>>> {
  return call(() =>
    api.PUT('/api/v1/tenants/{tenant_id}/logo', {
      params: { path: { tenant_id: tenantId } },
      // The schema types the part as a string (`format: binary`); the name
      // satisfies the type and the serialiser below sends the file itself.
      body: { file: file.name },
      bodySerializer: () => {
        const form = new FormData()
        form.append('file', file, file.name)
        return form
      },
    }),
  )
}

/** Remove the logo; the letter mark stands in again. */
export function deleteLogo(tenantId: string): Promise<ApiResult<Schema<'TenantResponse'>>> {
  return call(() =>
    api.DELETE('/api/v1/tenants/{tenant_id}/logo', {
      params: { path: { tenant_id: tenantId } },
    }),
  )
}
